import { Button, IconArrowStartLine, Modal, Spinner } from "@instructure/ui";
import { clear } from "idb-keyval";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useNotification } from "../../../../../components/CustomHooks/NotificationProvider";
import { AssignmentService } from "../../../../../services/AssignmentService";
import { sleep } from "../../../../../utils/Timer";
import ErrorBody from "./ErrorBody";
import ProgressBody from "./ProgressBody";
import SuccessBody from "./SuccessBody";
import TimeoutBody from "./TimeoutBody";

const IN_PROGRESS = "in_progress";
const SUCCESS = "success";
const ERROR = "error";
const TIMEOUT = "timeout";

const MAX_RETRIES = 50;
const RETRY_DELAY_MS = 5000;

const FinalizeVerifyModal = ({
  open,
  setOpen,
  courseCode,
  sectionCanvasId,
  resubmitFunction,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const showNotification = useNotification();
  const [status, setStatus] = useState(IN_PROGRESS);
  const [resubmitting, setResubmitting] = useState(false);
  const message = () => {
    switch (status) {
      case IN_PROGRESS:
        return t("finalizeCourse.finalizingResults");
      case SUCCESS:
        return t("finalizeCourse.resultsFinalized");
      case ERROR:
        return t("finalizeCourse.errorFinalizing");
      case TIMEOUT:
        return t("finalizeCourse.resultsTimeout");
      default:
        return "";
    }
  };

  useEffect(() => {
    async function handleVerification() {
      try {
        let verified = false;
        let attempts = 0;
        while (!verified && attempts < MAX_RETRIES) {
          attempts++;
          const isFinalized =
            await AssignmentService.getFinalizedResultsExist(sectionCanvasId);
          if (isFinalized) {
            verified = true;
          } else {
            await sleep(RETRY_DELAY_MS);
          }
        }
        if (verified) {
          setStatus(SUCCESS);
        } else {
          throw new Error("Finalization verification timed out");
        }
      } catch (error) {
        if (error.message === "Finalization verification timed out") {
          setStatus(TIMEOUT);
        } else {
          setStatus(ERROR);
        }
      }
    }
    if (open && status === IN_PROGRESS) {
      handleVerification();
    }
  }, [open, status, showNotification, courseCode, sectionCanvasId]);

  async function handleResubmit() {
    try {
      setResubmitting(true);
      await resubmitFunction();
      setStatus(IN_PROGRESS);
    } catch {
      showNotification(
        t("finalizeCourse.finalizeError", { course: courseCode }),
        ERROR
      );
    } finally {
      setResubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      label={message()}
      onExited={async () => {
        sessionStorage.clear();
        await clear();
        navigate(`/courses?courseId=${sectionCanvasId}`);
      }}
    >
      <Modal.Body>
        {status === IN_PROGRESS && <ProgressBody />}
        {status === SUCCESS && <SuccessBody />}
        {status === ERROR && <ErrorBody />}
        {status === TIMEOUT && <TimeoutBody />}
      </Modal.Body>
      {(status === SUCCESS || status === TIMEOUT) && (
        <Modal.Footer>
          <Button
            renderIcon={IconArrowStartLine}
            onClick={() => setOpen(false)}
          >
            {t("finalizeCourse.goToDashboard")}
          </Button>
        </Modal.Footer>
      )}
      {status === ERROR && (
        <Modal.Footer>
          <Button
            color="primary"
            onClick={handleResubmit}
            disabled={resubmitting}
          >
            {resubmitting ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              t("global.resubmit")
            )}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default FinalizeVerifyModal;
